// Kontakt — redesign dle handoveru Claude Design (2026-09-09).
//
// ⚠️ ZÁVAZNÝ ZDROJ: `design_handoff_kontakt/README.md` + prototypy
// `Kontakt redesign 2a.dc.html` (desktop 1240) a `… - mobil.dc.html` (390).
// NAHRAZUJE `design_handoff_jipos_redesign/screens/09-kontakt.md`
// i `m09-kontakt.md` — narazí-li budoucí session na tři kontaktní karty,
// blok „Napište nám", tmavý panel „Raději zavoláte?" nebo odznak „Plátci DPH"
// a bude je chtít vrátit, NEDĚLAT. Paritu hlídá `tools/audit/contact-parity.mjs`
// (0 rozdílů = hotovo).
//
// Nativní stránka je `type-page`; obsah z administrace nese strukturu
// předchozího správce:
//   div.contact-page > div.contact-container > h1 + div.contact-grid
//     div.contact-card ×3:
//       1) „E-shop"        — a.contact-email, ul > li.contact-item ×3 (odkazy), p „TIP: …"
//       2) „Prodejna Jipos" — .address-line, a.contact-map-link, .opening-hours-item ×7
//       3) „Fakturační údaje" — .billing-company, .billing-address,
//          .billing-detail-item (.billing-detail-label / -value)
//   h2 „Prodejna Jipos" + p + div.plus-gallery-wrap (11 fotek, nativní lightbox)
//   p „Videoprohlídka" + p > video[controls]
//   h2 „Kde nás najdete" + p > iframe (Google Maps embed)
// Adresa, otevírací doba, IČO/DIČ, fotky, video i mapa jsou proto REÁLNÉ
// (čtou se z obsahu), ne kopie z návrhu. Prototyp je zdroj VZHLEDU, ne dat.
//
// ROZHODNUTÍ A ODCHYLKY (proti dosavadnímu stavu i proti prototypu):
//   • H1 stránky je nově marketingový nadpis „Jsme tu pro vás" (prototyp).
//     Slovo „Kontakt" nese drobečková navigace a `<title>` z administrace.
//     Nativní `header[itemprop=headline]` se PŘESOUVÁ do hera a přepisuje se
//     mu text — schema.org headline tím zůstane vyplněný a v DOM je jeden H1.
//   • Kontaktní formulář ani jeho restyle už na stránce nejsou (handover §„Co
//     se mění" bod 1): kontakt = e-mail + telefon v hlavičce (desktop) resp.
//     v kartě „Ozvěte se nám" (mobil). Shop nemá v adminu definovaný formulář,
//     takže `#FORM[code=contact]#` stejně nikdy nevznikl.
//   • Sekce Tým má tři lidi (Veronika, Andrea, Nikola), bez jednatele
//     a bez reklamačního oddělení — reklamace a vrácení řeší dlaždice nahoře.
//   • Nadpis karty prodejny a text tipu se berou z ADMINISTRACE (prototyp má
//     „Prodejna nářadí Bílina" a vlastní znění tipu); konstanty níž jsou jen
//     fallback pro shop, který ta pole nemá. Parita text neměří.
//   • Otevírací doba PRODEJNY (karta prodejny) se slučuje do souvislých skupin
//     se stejným časem („Po–Pá", „So–Ne") a stav „teď otevřeno" se počítá
//     z aktuálního času. Když je zavřeno, drží obě skupiny neutrální šedou
//     (README §Interactions).
//   • ⚠️ Hlavička a mobilní karta „Ozvěte se nám" nesou pracovní dobu
//     ZÁKAZNICKÉ PODPORY (Po–Čt 8:00–15:00, Pá 7:30–14:30, `CONTACT.supportHours`) s popiskem,
//     ne dobu prodejny (zadání klienta 2026-09-15 — prototyp tam měl prodejnu
//     a zákazník čekal odpověď na e-mail i o víkendu). Karta prodejny má nad
//     bloky nadpis „Otevírací doba prodejny", ať jsou obě doby rozlišené.
//   • Hero fotka `max(368px, 20vw)` je z prototypu; README §1.1 píše 330 —
//     autoritativní je prototyp, podle něj měří paritní harness.
//   • Pás s fotkou je od #320 sdílená komponenta info stránek (`pageHero.js`,
//     `components/_page-hero.scss`, třídy `.bs-pghero*`) — kontakt je její
//     zdrojová podoba, ostatní stránky ji dostanou se záznamem v admin bloku
//     `#bs-page-photos`. Záznam `kontakt` vymění i fotku týmu.

import { icon } from './icon.js';
import { fillAvatar } from './teamPhoto.js';
import { MEDIA } from './instanceUrl.js';
import { NBSP } from './money.js';
import { CONTACT, openingHours, phonesOn, supportHours, telHref } from './headerTools.js';
import { FAQ, HOURS } from '../locale/cs/common.js';
import L from '../locale/cs/contactPage.js';
import SHOP from '../../config/shop.js';
import {
  el,
  link,
  textOf,
  iconBox,
  pageHost,
  pageIs,
  markReady,
  buildGallery,
  buildVideoBox,
  buildMapBox,
  sectionHead,
} from './infoPage.js';
import { page, withPages } from './pages.js';
import { adoptTitle, buildPageHero, currentPageSlug, heroCta, pagePhoto, takeCrumbs } from './pageHero.js';
import { asChatTrigger, chatFallbackHref, chatOn } from './supportChat.js';
import LC from '../locale/cs/supportChat.js';

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs.
// Hero fotka týmu (HERO_IMG) je společná oběma shopům (tatáž firma a tým).
const LABEL_H1 = L.h1;
const LABEL_PEREX = L.perex;
const LABEL_HOURS_NOTE = L.hoursNote;
const LABEL_FAQ = L.faqTitle;
const LABEL_FAQ_HINT = L.faqHint;
const LABEL_TIP = L.tipTitle;
const LABEL_TIP_TEXT = L.tipText;
const LABEL_REACH = L.reach;
const LABEL_STORE = L.storeFallback;
const LABEL_OPEN_NOW = L.openNow;
const LABEL_NAVIGATE = L.navigate;
const LABEL_STORE_MORE = L.storeMore;
const STORE_HREF = page('store');
const LABEL_BILLING = L.billing;
const BILLING_EMAIL = SHOP.contact.billingEmail;
const LABEL_TEAM = L.team;
const LABEL_TEAM_HINT = L.teamHint;
const LABEL_GALLERY = L.gallery;
const LABEL_VIDEO = L.video;
const LABEL_VIDEO_CAP = L.videoCap;
const LABEL_MAP = L.map;
const LABEL_MAP_CTA = L.mapCta;
// Týmová fotka v4 (2026-10-07, varianta A vybraná Tomášem): v3 bez osoby zcela
// vlevo. Codex dokreslil jen místo po ní (zeď, štěrk, tělo kolegyně za ní), tváře
// a nápisy zůstávají pixely originálu IMG_3949 (shoda SFace 0,97–1,0). Mobilní
// výřez je posunutý, ať skupina zůstane uprostřed. v3 (2026-09-15) už měla tváře
// z originálu, AI doplnila jen zeď vlevo; v2 z handoveru byla AI regenerace se
// změněnými tvářemi. Linka a měření: assets/team/README.md §Týmová fotka kontaktu.
// Nové jméno souboru, ne přepis v3: média na CDN mají dlouhou cache.
const HERO_IMG = 'tym-jipos-v4-1800.jpg';
const HERO_IMG_M = 'tym-jipos-v4-mobil.jpg';
const LABEL_HERO_ALT = L.heroAlt;

// Tři nejčastější dotazy podpory (handover §1.2). `re` páruje odkaz z obsahu
// administrace — když ho stránka nese, vyhraje její href (slug se může lišit
// per shop); `href` je fallback z návrhu. Regexy zůstávají v kódu (S3 #399):
// matchují ADMIN OBSAH (typicky pořád v CZ, viz dev/sk/contact.html), ne
// pevný text platformy — nejsou to texty, které bychom sami vypisovali.
// ⚠️ VĚDOMÁ ODCHYLKA (2026-09-15, rozhodnutí Tomáše): 4. dlaždice „Časté dotazy"
// na /caste-dotazy/ — prototyp má tři. Regex páruje jen slug nebo „časté dotazy",
// holé „dotaz" by chytilo i odkaz typu „Máte dotaz? Napište nám".
const TILES = withPages([
  {
    icon: 'truck',
    title: L.tiles[0].title,
    sub: L.tiles[0].sub,
    href: page('orderStatus'),
    re: L.tileRe[0],
    primary: true,
  },
  { icon: 'shield', title: L.tiles[1].title, sub: L.tiles[1].sub, href: page('claim'), re: L.tileRe[1] },
  {
    icon: 'refresh',
    title: L.tiles[2].title,
    sub: L.tiles[2].sub,
    href: page('withdrawal'),
    re: L.tileRe[2],
  },
  { icon: 'help', title: FAQ.tileTitle, sub: FAQ.tileSub, href: FAQ.href, re: L.tileRe[3] },
]);

// Sekce Tým (handover §1.4) = `contact.team` z manifestu shopu (jména, role,
// casting fotek). Jména jen křestní (zadání Tomáše 2026-09-10); všechny karty
// vedou na společnou schránku `CONTACT.email` (osobní adresy se na webu
// neuvádějí, klient 2026-09-15).
const TEAM = SHOP.contact.team.map((m) => Object.assign({}, m, { email: CONTACT.email }));

const DAY_NUM = { Ne: 0, Po: 1, Út: 2, St: 3, Čt: 4, Pá: 5, So: 6 };

function cardByHeading(about, re) {
  const cards = about.querySelectorAll('.contact-card');
  for (let i = 0; i < cards.length; i += 1) {
    if (re.test(textOf(cards[i].querySelector('h2, h3')))) return cards[i];
  }
  return null;
}


// --- otevírací doba --------------------------------------------------------
// „07:30–17:00" → „7:30 – 17:00" (plné časy dle README, NBSP kolem pomlčky, ať
// se čas nezalomí uprostřed).
function formatRange(time) {
  return String(time)
    .replace(/\s*[–-]\s*/, NBSP + '–' + NBSP)
    .replace(/\b0(\d):/g, '$1:');
}

// „07:30–17:00" → [7.5, 17] pro výpočet stavu „teď otevřeno"
function parseRange(time) {
  const m = String(time).match(/(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return [+m[1] + +m[2] / 60, +m[3] + +m[4] / 60];
}

/**
 * Sloučí sousední dny se stejným časem do skupin („Po–Pá", „So–Ne") a označí
 * tu, která má PRÁVĚ TEĎ otevřeno. Vstup = řádky z administrace
 * (`hoursFromList`), pořadí dnů je pořadí v obsahu.
 * @returns {{label:string, time:string, open:boolean, now:boolean}[]}
 */
function groupHours(rows, now) {
  const d = now || new Date();
  const today = d.getDay();
  const hour = d.getHours() + d.getMinutes() / 60;
  const runs = [];
  rows.forEach(function (row) {
    const last = runs[runs.length - 1];
    if (last && last.raw === row.time && last.closed === !!row.closed) last.days.push(row.day);
    else runs.push({ raw: row.time, closed: !!row.closed, days: [row.day] });
  });
  return runs.map(function (run) {
    const range = run.closed ? null : parseRange(run.raw);
    const hasToday = run.days.some(function (day) {
      return DAY_NUM[day] === today;
    });
    return {
      label: run.days.length > 1 ? run.days[0] + '–' + run.days[run.days.length - 1] : run.days[0],
      time: run.closed ? run.raw : formatRange(run.raw),
      open: !run.closed,
      now: !!(hasToday && range && hour >= range[0] && hour < range[1]),
    };
  });
}

// Bez rozpisu v obsahu (jiná struktura stránky) jede projektová konstanta —
// tatáž doba prodejny, jakou zná model dopravy. `rows` = jiný rozpis
// (pracovní doba podpory pro hlavičku), zavřené dny se u něj vynechávají.
function hoursFallback(now, rows) {
  return (rows || openingHours(now)).map(function (row) {
    const range = row.open ? parseRange(row.value) : null;
    const d = now || new Date();
    const hour = d.getHours() + d.getMinutes() / 60;
    return {
      label: row.label,
      time: row.open ? formatRange(row.value) : row.value,
      open: row.open,
      now: !!(row.today && range && hour >= range[0] && hour < range[1]),
    };
  });
}

function readHours(storeSrc, now) {
  const items = storeSrc ? storeSrc.querySelectorAll('.opening-hours-item') : [];
  const rows = [];
  for (let i = 0; i < items.length; i += 1) {
    const day = textOf(items[i].querySelector('.opening-hours-day'));
    const closed = items[i].querySelector('.opening-hours-closed');
    const time = closed ? textOf(closed) : textOf(items[i].querySelector('.opening-hours-time'));
    if (day) rows.push({ day: day, time: time, closed: !!closed });
  }
  return rows.length ? groupHours(rows, now) : hoursFallback(now);
}

// Pracovní doba zákaznické podpory ve tvaru skupin hlavičky (jen pracovní dny).
function readSupportHours(now) {
  return hoursFallback(now, supportHours(now)).filter(function (g) {
    return g.open;
  });
}

// --- hero ------------------------------------------------------------------
// Pás s fotkou, drobečky, nadpis a pilulky = sdílená komponenta pageHero.js
// (#320); kontakt do ní dává obsah a pracovní dobu podpory navíc.
function buildHeroHours(hours) {
  const box = el('div', 'bs-ct__hh bs-ct__hh--labeled');
  const row = el('div', 'bs-ct__hh-row');
  // popisek „Zákaznická podpora" — doba na fotce patří podpoře, ne prodejně
  row.appendChild(el('span', 'bs-ct__hh-label', HOURS.support));
  hours.forEach(function (g, i) {
    if (i) row.appendChild(el('span', 'bs-ct__hh-sep', '|'));
    const group = el('span', 'bs-ct__hh-g' + (g.now ? ' is-now' : ''));
    // Tečka je uvnitř skupiny, která má otevřeno — o víkendu se zelená přesune
    // na So–Ne, ne aby svítila u zavřené Po–Pá (README §Interactions).
    if (g.now) group.appendChild(el('span', 'bs-ct__hh-dot'));
    const text = el('span', 'bs-ct__hh-text', g.label + ' ');
    text.appendChild(el('span', 'bs-ct__hh-time', g.time));
    group.appendChild(text);
    row.appendChild(group);
  });
  box.appendChild(row);
  box.appendChild(el('div', 'bs-ct__hh-note', LABEL_HOURS_NOTE));
  return box;
}

// Pilulka s ikonou a popiskem (tlačítka karty „Ozvěte se nám"; pilulky na
// fotce staví `heroCta()` z pageHero.js).
function ctaLink(href, label, iconName, cls) {
  const a = link(href, cls);
  a.appendChild(icon(iconName));
  a.appendChild(el('span', null, label));
  return a;
}

// Fotka týmu je výchozí fotka kontaktu; záznam stránky v admin bloku
// `#bs-page-photos` ji vymění (pageHero.js), jinak zůstává dnešní.
function heroPhoto() {
  return (
    pagePhoto(currentPageSlug()) || {
      src: MEDIA + HERO_IMG,
      srcMobile: MEDIA + HERO_IMG_M,
      alt: LABEL_HERO_ALT,
      width: 1800,
      height: 1013,
    }
  );
}

function buildHero(host, hours) {
  // Drobečky PŘED přepisem nadpisu — náhradní drobečky (bez nativních) nesou
  // původní text H1, ne marketingový nadpis.
  const crumbs = takeCrumbs(textOf(host.h1) || L.crumbFallback);
  // kanály v pořadí chat → e-mail → telefon; s chatem e-mail ustoupí (ghost)
  const chat = chatOn();
  const ctas = el('div', 'bs-pghero__ctas');
  if (chat) {
    const chatBtn = heroCta(chatFallbackHref(), LC.cta, 'chat', 'primary');
    asChatTrigger(chatBtn, 'contact-hero');
    ctas.appendChild(chatBtn);
  }
  ctas.appendChild(heroCta('mailto:' + CONTACT.email, CONTACT.email, 'mail', chat ? 'ghost' : 'primary'));
  if (phonesOn()) ctas.appendChild(heroCta(telHref(CONTACT.phone), CONTACT.phone, 'phone', 'ghost'));
  return buildPageHero({
    photo: heroPhoto(),
    crumbs: crumbs,
    content: [
      // Nativní H1 (schema.org headline) se přesouvá a nese marketingový
      // nadpis návrhu — slovo „Kontakt" zůstává v drobečkách a v title stránky.
      adoptTitle(host.h1, LABEL_H1),
      el('p', 'bs-pghero__perex', LABEL_PEREX),
      ctas,
      buildHeroHours(hours),
    ],
  });
}

// --- „Nejčastější dotazy vyřešíte hned" ------------------------------------
function adminLinks(shopSrc) {
  const out = [];
  if (!shopSrc) return out;
  shopSrc.querySelectorAll('li a[href]').forEach(function (a) {
    const li = a.closest('li');
    out.push({ href: a.getAttribute('href'), text: textOf(li) });
  });
  return out;
}

function buildFaq(shopSrc) {
  const sec = el('section', 'bs-ct__faq');
  const head = el('div', 'bs-ct__faq-head');
  head.appendChild(el('h2', 'bs-ct__faq-title', LABEL_FAQ));
  head.appendChild(el('span', 'bs-ct__faq-hint', LABEL_FAQ_HINT));
  sec.appendChild(head);

  const links = adminLinks(shopSrc);
  const grid = el('div', 'bs-ct__tiles');
  TILES.forEach(function (def) {
    const hit = links.filter(function (l) {
      return def.re.test(l.text) || def.re.test(l.href);
    })[0];
    const tile = link(hit ? hit.href : def.href, 'bs-ct__tile' + (def.primary ? ' bs-ct__tile--primary' : ''));
    tile.appendChild(iconBox(def.icon, 'bs-ct__tile-ic'));
    const body = el('span', 'bs-ct__tile-body');
    body.appendChild(el('span', 'bs-ct__tile-title', def.title));
    body.appendChild(el('span', 'bs-ct__tile-sub', def.sub));
    tile.appendChild(body);
    tile.appendChild(icon('arrow-right', 'bs-ct__tile-chev'));
    grid.appendChild(tile);
  });
  sec.appendChild(grid);

  // Text tipu z administrace (bez prefixu „TIP:"), jinak znění návrhu
  const src = shopSrc ? shopSrc.querySelector('p') : null;
  const tipText = (textOf(src) || '').replace(/^TIP:\s*/i, '') || LABEL_TIP_TEXT;
  const tip = el('div', 'bs-ct__tip');
  tip.appendChild(iconBox('info', 'bs-ct__tip-ic'));
  const tipBody = el('div', 'bs-ct__tip-body');
  tipBody.appendChild(el('div', 'bs-ct__tip-title', LABEL_TIP));
  tipBody.appendChild(el('p', 'bs-ct__tip-text', tipText));
  tip.appendChild(tipBody);
  sec.appendChild(tip);
  return sec;
}

// --- „Ozvěte se nám" (jen mobil) -------------------------------------------
function buildReach(hours) {
  const sec = el('section', 'bs-ct__reach');
  const head = el('div', 'bs-ct__reach-head');
  head.appendChild(el('h2', 'bs-ct__reach-label', LABEL_REACH));
  // pracovní doba PODPORY (kanály karty jsou e-mail/telefon podpory)
  const open = hours.filter(function (g) {
    return g.open;
  })[0];
  if (open) head.appendChild(el('span', 'bs-ct__reach-hours', HOURS.supportShort + ' ' + open.label + ' ' + open.time));
  sec.appendChild(head);

  // kanály v pořadí chat → e-mail → telefon; s chatem e-mail ustoupí (out)
  const chat = chatOn();
  const btns = el('div', 'bs-ct__reach-btns');
  if (chat) {
    const chatBtn = ctaLink(chatFallbackHref(), LC.cta, 'chat', 'bs-ct__reach-btn bs-ct__reach-btn--primary');
    asChatTrigger(chatBtn, 'contact-reach');
    btns.appendChild(chatBtn);
  }
  btns.appendChild(
    ctaLink(
      'mailto:' + CONTACT.email,
      CONTACT.email,
      'mail',
      'bs-ct__reach-btn bs-ct__reach-btn--' + (chat ? 'out' : 'primary')
    )
  );
  if (phonesOn()) btns.appendChild(ctaLink(telHref(CONTACT.phone), CONTACT.phone, 'phone', 'bs-ct__reach-btn bs-ct__reach-btn--out'));
  sec.appendChild(btns);
  return sec;
}

// --- prodejna + fakturační údaje -------------------------------------------
function buildHoursBlocks(hours) {
  const box = el('div', 'bs-ct__hours');
  hours.forEach(function (g) {
    const block = el('div', 'bs-ct__hblock' + (g.now ? ' is-now' : ''));
    const label = el('div', 'bs-ct__hblock-label');
    if (g.now) label.appendChild(el('span', 'bs-ct__hblock-dot'));
    label.appendChild(el('span', null, g.now ? g.label + ' · ' + LABEL_OPEN_NOW : g.label));
    block.appendChild(label);
    block.appendChild(el('div', 'bs-ct__hblock-time', g.time));
    box.appendChild(block);
  });
  return box;
}

function buildStoreCard(storeSrc, hours, hasMap) {
  const card = el('article', 'bs-ct__store');

  const head = el('div', 'bs-ct__store-head');
  head.appendChild(iconBox('store', 'bs-ct__store-ic')); // jen mobil (CSS)
  const headText = el('div', 'bs-ct__store-headtext');
  headText.appendChild(
    el('h2', 'bs-ct__store-title', textOf(storeSrc ? storeSrc.querySelector('h2, h3') : null) || LABEL_STORE)
  );
  const address = textOf(storeSrc ? storeSrc.querySelector('.address-line') : null);
  if (address) {
    const row = el('div', 'bs-ct__store-addr');
    row.appendChild(icon('pin', 'bs-ct__store-pin'));
    row.appendChild(el('span', null, address));
    headText.appendChild(row);
  }
  head.appendChild(headText);
  card.appendChild(head);

  card.appendChild(el('h3', 'bs-ct__hours-title', HOURS.store));
  card.appendChild(buildHoursBlocks(hours));

  const map = storeSrc ? storeSrc.querySelector('a.contact-map-link, a[href*="map"]') : null;
  const ctas = el('div', 'bs-ct__store-ctas');
  // S embedem mapy na stránce míří CTA na naši sekci `#mapa`, jinak do map
  const navHref = hasMap ? '#mapa' : map ? map.getAttribute('href') : '';
  if (navHref) {
    const nav = link(navHref, 'bs-ct__btn bs-ct__btn--ink');
    if (!hasMap) {
      nav.target = '_blank';
      nav.rel = 'noopener noreferrer';
    }
    nav.appendChild(icon('map'));
    nav.appendChild(el('span', null, LABEL_NAVIGATE));
    ctas.appendChild(nav);
  }
  // shop bez stránky prodejny (SK) tlačítko nedostane — vedlo by na href="null"
  if (STORE_HREF) {
    const more = link(STORE_HREF, 'bs-ct__btn bs-ct__btn--out');
    more.appendChild(el('span', null, LABEL_STORE_MORE));
    more.appendChild(icon('arrow-right'));
    ctas.appendChild(more);
  }
  card.appendChild(ctas);
  return card;
}

function buildBillingCard(billingSrc) {
  const card = el('article', 'bs-ct__billing');
  card.appendChild(el('h2', 'bs-ct__billing-label', LABEL_BILLING));

  const company = textOf(billingSrc ? billingSrc.querySelector('.billing-company') : null);
  if (company) card.appendChild(el('div', 'bs-ct__company', company));

  const addr = billingSrc ? billingSrc.querySelector('.billing-address') : null;
  if (addr) {
    const box = el('div', 'bs-ct__billing-addr');
    box.innerHTML = addr.innerHTML; // `<br>` drží dvouřádkové zalomení z administrace
    card.appendChild(box);
  }

  const ids = el('div', 'bs-ct__ids');
  if (billingSrc) {
    billingSrc.querySelectorAll('.billing-detail-item').forEach(function (item) {
      const label = textOf(item.querySelector('.billing-detail-label'));
      const value = textOf(item.querySelector('.billing-detail-value'));
      if (!label && !value) return;
      const chip = el('span', 'bs-ct__id');
      chip.appendChild(el('span', 'bs-ct__id-label', L.idLabel(label)));
      chip.appendChild(el('span', 'bs-ct__id-value', value));
      ids.appendChild(chip);
    });
  }
  if (ids.children.length) card.appendChild(ids);

  const mail = link('mailto:' + BILLING_EMAIL, 'bs-ct__billing-mail');
  mail.appendChild(icon('mail'));
  mail.appendChild(el('span', null, BILLING_EMAIL));
  card.appendChild(mail);
  return card;
}

// --- sekce Tým -------------------------------------------------------------
function buildTeamSection() {
  const sec = el('section', 'bs-ct__team');
  sec.appendChild(sectionHead(LABEL_TEAM, LABEL_TEAM_HINT, 'bs-ct__team-head'));

  const grid = el('div', 'bs-ct__team-grid');
  TEAM.forEach(function (m) {
    const card = el('article', 'bs-ct__member');
    card.appendChild(fillAvatar(el('span', 'bs-ct__member-photo'), m.slot, 56, m.icon));

    const body = el('div', 'bs-ct__member-body');
    body.appendChild(el('div', 'bs-ct__member-name', m.name));
    body.appendChild(el('div', 'bs-ct__member-role', m.role));

    const rows = el('div', 'bs-ct__member-rows');
    const mail = link('mailto:' + m.email, 'bs-ct__member-row bs-ct__member-row--mail');
    mail.appendChild(icon('mail'));
    mail.appendChild(el('span', null, m.email));
    rows.appendChild(mail);

    // Přímé linky zatím nejsou — centrální číslo. Mobilní návrh telefon
    // nemá (na dotyku volá pilulka e-mailu), CSS ho tam skrývá.
    const phone = m.phone || CONTACT.phone;
    if (phonesOn()) {
      const tel = link(telHref(phone), 'bs-ct__member-row bs-ct__member-row--tel');
      tel.appendChild(icon('phone'));
      tel.appendChild(el('span', null, phone));
      rows.appendChild(tel);
    }

    body.appendChild(rows);
    card.appendChild(body);
    grid.appendChild(card);
  });
  sec.appendChild(grid);
  return sec;
}

// ---------------------------------------------------------------------------
export function initContactPage() {
  if (!pageIs('contact')) return;
  const host = pageHost();
  if (!host) return;
  if (document.querySelector('.bs-ct')) return; // idempotence
  const about = host.about;

  const shopSrc = cardByHeading(about, L.cardShopRe);
  const storeSrc = cardByHeading(about, L.cardStoreRe);
  const billingSrc = cardByHeading(about, L.cardBillingRe);
  if (!shopSrc && !storeSrc) return; // jiná struktura obsahu → nešaháme

  const hours = readHours(storeSrc);
  const support = readSupportHours();
  const hasMap = !!about.querySelector('iframe');

  const root = el('div', 'bs-ip bs-ct');
  root.appendChild(buildHero(host, support));
  root.appendChild(buildFaq(shopSrc));
  root.appendChild(buildReach(support));

  const cols = el('section', 'bs-ct__cols');
  cols.appendChild(buildStoreCard(storeSrc, hours, hasMap));
  cols.appendChild(buildBillingCard(billingSrc));
  root.appendChild(cols);

  root.appendChild(buildTeamSection());

  // fotogalerie prodejny (reálné přílohy, nativní lightbox)
  const anchors = Array.prototype.slice.call(about.querySelectorAll('.plus-gallery-item > a[href]'));
  if (anchors.length) {
    root.appendChild(
      buildGallery(anchors, {
        title: LABEL_GALLERY,
        hint: textOf(storeSrc ? storeSrc.querySelector('.address-line') : null),
        visible: 5,
        visibleM: 3, // mobilní návrh ukazuje 3 dlaždice (+N na třetí)
        alt: L.galleryAlt,
      })
    );
  }

  // video a mapa vedle sebe
  const media = el('section', 'bs-ct__media');
  const video = about.querySelector('video');
  if (video) media.appendChild(buildVideoBox(video, { title: LABEL_VIDEO, caption: LABEL_VIDEO_CAP }));

  const frame = about.querySelector('iframe[src*="map"], iframe');
  if (frame) {
    const mapSec = el('section', 'bs-ct__map-sec');
    mapSec.id = 'mapa';
    mapSec.appendChild(sectionHead(LABEL_MAP, null, 'bs-ct__map-head'));
    const mapLink = about.querySelector('a.contact-map-link, a[href*="map"]');
    mapSec.appendChild(
      buildMapBox(frame, {
        title: textOf(storeSrc ? storeSrc.querySelector('h2, h3') : null) || LABEL_STORE,
        address: textOf(storeSrc ? storeSrc.querySelector('.address-line') : null),
        href: mapLink ? mapLink.getAttribute('href') : '',
        label: LABEL_MAP_CTA,
      })
    );
    media.appendChild(mapSec);
  }
  if (media.children.length) root.appendChild(media);

  about.classList.add('bs-ip__native');
  about.insertAdjacentElement('beforebegin', root);
  markReady();
}
